import { Box, Grid, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { API } from 'api';
import { getCameraActions, PageLayout } from 'components/PageLayout';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQueryParams } from 'hooks/useQueryParams';

type PassthroughProps = {
  method: 'POST' | 'GET';
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    padding: theme.spacing(2),
    margin: '0 auto',
    overflowY: 'auto',
    width: '100%',
    maxWidth: '1000px',
  },
}));

type PassthroughParams = {
  route: string;
};

type ContentProps = {
  method: 'GET' | 'POST';
};

const Content = (props: ContentProps): JSX.Element => {
  const { classes } = useStyles();
  const { method } = props;
  const { t } = useTranslation();
  const { route } = useParams<PassthroughParams>();
  const [restParams] = useQueryParams();
  const [message, setMessage] = useState('pending');

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        if (method === 'POST') {
          await API.post(`/${route}`, restParams);
        } else {
          await API.get(`/${route}`, { params: restParams });
        }
        setMessage(t('passthrough.success'));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setMessage(JSON.stringify(error.response.data) || 'An error occurred');
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, method, JSON.stringify(restParams)]);

  return (
    <>
      <Helmet>
        <title>{t('passthrough.title')}</title>
      </Helmet>

      <Paper elevation={0} className={classes.paper}>
        <Grid
          container
          spacing={2}
          sx={{ width: '100%', height: '100%', justifyContent: 'center' }}
        >
          <Grid size={12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h1">{t('passthrough.title')}</Typography>
            </Box>
          </Grid>
          <Grid size={12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h1">{message}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export const Passthrough = (props: PassthroughProps): JSX.Element => {
  const { method } = props;
  const { t } = useTranslation();

  return (
    <PageLayout
      data-cname="PassthroughPage"
      headerBarProps={{
        action: '/',
        title: t('passthrough.title'),
        iconActions: getCameraActions(t),
      }}
    >
      <Content method={method} />
    </PageLayout>
  );
};
